import { useState } from 'react';
import { ApiError } from '../api/client';
import {
  useAddManagedEntity,
  useManagedEntities,
  useRemoveManagedEntity,
  useSearchGameEntities,
  useWorldOptions,
} from '../api/queries';
import type { GameEntitySearchResult, ManagedEntityKind, User } from '../api/types';
import { Spinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { useDebounced } from '../hooks/useDebounced';

const KIND_LABEL: Record<ManagedEntityKind, string> = { player: 'Spieler', tribe: 'Stamm' };

/**
 * Verwaltung-Seite: nur für Stammesführer und Administratoren (serverseitig
 * über `requireTribeLeaderOrAdmin` durchgesetzt, siehe `App.tsx` für die
 * Zugriffsprüfung davor). Eine gemeinsame Liste je Welt -- kein privates
 * Konzept eines einzelnen Stammesführers, mehrere teilen sich dieselbe.
 */
export function ManagementPage({ user }: { user: User }) {
  // Weltübergreifende Administratoren müssen die Welt selbst wählen;
  // Stammesführer sind an ihre eigene gebunden.
  const needsWorldChoice = user.world === null;
  const [worldKey, setWorldKey] = useState('');
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;

  const entities = useManagedEntities(activeWorld || undefined);
  const remove = useRemoveManagedEntity(activeWorld || undefined);
  const toast = useToast();

  const confirmRemove = (id: number, label: string) => {
    if (!window.confirm(`"${label}" von der Verwaltung-Liste entfernen?`)) return;
    remove.mutate(
      { id },
      {
        onSuccess: () => toast.push('success', `"${label}" wurde entfernt.`),
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  const rows = entities.data?.entities ?? [];

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>Verwaltung</h1>
          <p className="muted">
            Spieler und Stämme, die dein Stamm im Blick behält — eine gemeinsame Liste für alle
            Stammesführer dieser Welt.
          </p>
        </div>
      </div>

      {needsWorldChoice && (
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setWorldKey(event.target.value)} required>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {needsWorldChoice && !worldKey && (
        <p className="muted center pad">Wähle eine Welt, um ihre Verwaltung-Liste zu sehen.</p>
      )}

      {activeWorld && (
        <>
          <AddEntityForm worldKey={activeWorld} />

          {entities.isPending && <Spinner label="Liste wird geladen …" />}

          {entities.isError && (
            <div className="flash flash-error">
              {entities.error instanceof ApiError
                ? entities.error.message
                : 'Liste konnte nicht geladen werden.'}
            </div>
          )}

          {!entities.isPending && rows.length === 0 && (
            <p className="muted center pad">Noch keine Spieler oder Stämme auf der Liste.</p>
          )}

          {rows.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Art</th>
                    <th>Name</th>
                    <th>Hinzugefügt von</th>
                    <th>Am</th>
                    <th className="right">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((entity) => {
                    const label = entity.tag ? `${entity.name ?? '?'} (${entity.tag})` : (entity.name ?? '?');
                    return (
                      <tr key={entity.id}>
                        <td>{KIND_LABEL[entity.kind]}</td>
                        <td>
                          {entity.name === null ? (
                            <span className="muted" title="In den zuletzt synchronisierten Weltdaten nicht (mehr) gefunden.">
                              Unbekannt (ID {entity.gameId})
                            </span>
                          ) : (
                            label
                          )}
                        </td>
                        <td className="muted">{entity.addedByName ?? '—'}</td>
                        <td className="nowrap">{new Date(entity.createdAt).toLocaleDateString('de-DE')}</td>
                        <td className="right">
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            disabled={remove.isPending}
                            onClick={() => confirmRemove(entity.id, label)}
                          >
                            Entfernen
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function AddEntityForm({ worldKey }: { worldKey: string }) {
  const [kind, setKind] = useState<ManagedEntityKind>('player');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<GameEntitySearchResult | null>(null);
  const debouncedQuery = useDebounced(query);

  const search = useSearchGameEntities(worldKey, kind, debouncedQuery);
  const add = useAddManagedEntity(worldKey);
  const toast = useToast();

  const results = search.data?.results ?? [];
  // Ausgewähltes/schon eingetragenes nicht nochmal in der Trefferliste anbieten.
  const showResults = debouncedQuery.length > 0 && !selected;

  const pick = (result: GameEntitySearchResult) => {
    setSelected(result);
    setQuery(result.tag ? `${result.name} (${result.tag})` : result.name);
  };

  const changeQuery = (value: string) => {
    setQuery(value);
    setSelected(null);
  };

  const changeKind = (value: ManagedEntityKind) => {
    setKind(value);
    setQuery('');
    setSelected(null);
  };

  const submit = () => {
    if (!selected) return;
    add.mutate(
      { kind, gameId: selected.gameId },
      {
        onSuccess: () => {
          toast.push('success', `"${selected.name}" wurde hinzugefügt.`);
          setQuery('');
          setSelected(null);
        },
        onError: (error) => toast.push('error', error.message),
      },
    );
  };

  return (
    <div className="form" style={{ marginBottom: '16px' }}>
      <div className="field-row">
        <label className="field">
          <span>Art</span>
          <select value={kind} onChange={(event) => changeKind(event.target.value as ManagedEntityKind)}>
            <option value="player">Spieler</option>
            <option value="tribe">Stamm</option>
          </select>
        </label>

        <label className="field grow" style={{ position: 'relative' }}>
          <span>{kind === 'player' ? 'Spielername' : 'Stammesname oder -tag'}</span>
          <input
            type="text"
            value={query}
            onChange={(event) => changeQuery(event.target.value)}
            placeholder="Zum Suchen tippen …"
            autoComplete="off"
          />

          {showResults && (
            <div className="autocomplete-list">
              {search.isFetching && <div className="autocomplete-hint">Wird gesucht …</div>}
              {!search.isFetching && results.length === 0 && (
                <div className="autocomplete-hint">Keine Treffer in den Weltdaten.</div>
              )}
              {results.map((result) => (
                <button
                  type="button"
                  key={result.gameId}
                  className="autocomplete-item"
                  onClick={() => pick(result)}
                >
                  {result.name}
                  {result.tag && <span className="muted"> ({result.tag})</span>}
                </button>
              ))}
            </div>
          )}
        </label>

        <button type="button" className="btn btn-primary" disabled={!selected || add.isPending} onClick={submit}>
          {add.isPending ? 'Wird hinzugefügt …' : 'Hinzufügen'}
        </button>
      </div>
      <small className="hint">
        Namen kommen aus den zuletzt synchronisierten Weltdaten — wähle einen Treffer aus der Liste.
      </small>
    </div>
  );
}
